import { z } from 'zod';

export const normalizeEventDate = (value: string): string | null => {
  const cleanValue = value.trim();
  const isoMatch = cleanValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const localMatch = cleanValue.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  const match = isoMatch || localMatch;

  if (!match) return null;

  const year = Number(isoMatch ? match[1] : match[3]);
  const month = Number(isoMatch ? match[2] : match[1]);
  const day = Number(isoMatch ? match[3] : match[2]);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() + 1 !== month ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

export const normalizeEventTime = (value: string): string | null => {
  const cleanValue = value.trim().toUpperCase().replace(/\s+/g, '');
  const match = cleanValue.match(/^(\d{1,2})(?::(\d{2}))?([AP]M)?$/);

  if (!match) return null;

  let hour = Number(match[1]);
  const minute = Number(match[2] || '00');
  const meridiem = match[3];

  if (minute > 59) return null;

  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    if (meridiem === 'AM') {
      if (hour === 12) hour = 0;
    } else if (hour !== 12) {
      hour += 12;
    }
  } else if (hour > 23) {
    return null;
  }

  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

export const EventDateSchema = z.string()
  .trim()
  .min(1, "Start Date must be provided.")
  .superRefine((value, context) => {
    if (value && !normalizeEventDate(value)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Use a real calendar date in YYYY-MM-DD or MM/DD/YYYY format.",
      });
    }
  })
  .transform((value) => normalizeEventDate(value) || value);

const eventTimeSchema = (label: "Start" | "End") => z.string()
  .trim()
  .min(1, `${label} Time must be provided.`)
  .superRefine((value, context) => {
    if (value && !normalizeEventTime(value)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${label} Time must use a valid time such as 9:00 AM or 14:30.`,
      });
    }
  })
  .transform((value) => normalizeEventTime(value) || value);

export const EventStartTimeSchema = eventTimeSchema("Start");
export const EventEndTimeSchema = eventTimeSchema("End");

export const EventParticipantSchema = z.object({
  id: z.string().min(1, "Participant ID must be a non-empty string"), // Just a non-empty string
  rank: z.number().int().min(1, "Rank must be a positive integer"),  // Ensure it's a positive integer starting from 1
});

export const EventSchema = z.object({
  id: z.string().optional(),
  eventName: z.string()
    .min(5, "Event Name must be at least 5 characters long.")
    .max(100, "Event Name must be less than 100 characters long."),
  description: z.string()
    .max(775, "Description must be 775 characters or fewer."),
  location: z.string()
    .min(2, "Location must be at least 2 characters long.")
    .max(100, "Location must be less than 100 characters long."),
  startDate: EventDateSchema,
  startTime: EventStartTimeSchema,
  endTime: EventEndTimeSchema,
  gatheringID: z.string(),
  eventParticipants: z.array(EventParticipantSchema).optional(), // Now an array of {id, rank} objects
  eventType: z.string().max(20, "Event Type must be less than 20 characters.").optional(),
  childFriendly: z.boolean().optional(), // Optional boolean

});

// Keep the add/edit form on the same rules as every other event entry point,
// without requiring it to validate fields (such as participants) that it does
// not expose to the user.
export const EditableEventSchema = EventSchema.pick({
  eventName: true,
  description: true,
  location: true,
  startDate: true,
  startTime: true,
  endTime: true,
  eventType: true,
  childFriendly: true,
});

export type Event = z.infer<typeof EventSchema>;
